import { useCallback, useRef, useState } from 'react';
import { validateUserIdentification, isFullNameValid, isEmailValid, isCpfValid, isPhoneValid, isBirthDateValid } from '../utils/validation';
import type { UserIdentificationFormProps, UserIdentificationFormState } from '../types/user-identification.types';
import type { UserIdentificationData } from '../../../../types';
export const useUserIdentification = (props: UserIdentificationFormProps) => {
  const { initialData, onSave, onContactCaptured } = props;

  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState<Partial<UserIdentificationData>>({});
  const lastCapturedContactRef = useRef('');

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name === 'homephone' ? 'phone' : name]: value 
    }));
    if (errors[name as keyof UserIdentificationData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  }, [errors]);

  const validate = useCallback((): boolean => {
    const state: UserIdentificationFormState = {
      formData,
      errors,
    };
    
    const newErrors = validateUserIdentification(state);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, errors]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  }, [validate, formData, onSave]);

  // Captura de contato no blur: se email OU telefone estão válidos, avisa o pai
  // (que alimenta o cart pra recuperação) — mesmo sem o cliente clicar em "continuar".
  // Dedupe por valor: blur repetido no mesmo email/telefone não re-dispara.
  const handleContactBlur = useCallback(() => {
    const emailOk = isEmailValid(formData.email) && formData.email.trim();
    const phoneOk = isPhoneValid(formData.phone) && formData.phone.trim();
    if (!emailOk && !phoneOk) return;

    const signature = `${formData.email.trim()}|${formData.phone.trim()}`;
    if (signature === lastCapturedContactRef.current) return;
    lastCapturedContactRef.current = signature;

    onContactCaptured?.(formData);
  }, [formData, onContactCaptured]);

  // Field validation helpers
  const isFullNameValidState = isFullNameValid(formData.fullName);
  const isEmailValidState = isEmailValid(formData.email);
  const isCpfValidState = isCpfValid(formData.cpf || '', formData.cpfOnIdentification);
  const isPhoneValidState = isPhoneValid(formData.phone);
  const isBirthDateValidState = formData.requestBirthDate
    ? isBirthDateValid(formData.birth || '')
    : true;

  return {
    formData,
    errors,
    isFullNameValid: isFullNameValidState,
    isEmailValid: isEmailValidState,
    isCpfValid: isCpfValidState,
    isPhoneValid: isPhoneValidState,
    isBirthDateValid: isBirthDateValidState,
    handleChange,
    handleSubmit,
    handleContactBlur,
  };
};